import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Plus, Copy, Check, Trash2 } from 'lucide-react'
import { getApiKeys, createApiKey, revokeApiKey } from '../api/buzz'
import { getStorageApiKeys, createStorageApiKey, revokeStorageApiKey } from '../api/storage'
import { getBrainerApiKeys, createBrainerApiKey, revokeBrainerApiKey } from '../api/brainer'
import { getLensApiKeys, createLensApiKey, revokeLensApiKey } from '../api/lens'
import { Badge } from '../components/Badge'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import type { BuzzApiKeyCreated, StorageApiKeyCreated, BrainerApiKeyCreated, LensApiKeyCreated } from '../types'

function keyStatus(isActive: boolean, expiresAt: string | null): 'active' | 'expired' | 'revoked' {
  if (!isActive) return 'revoked'
  if (expiresAt && new Date(expiresAt) < new Date()) return 'expired'
  return 'active'
}

const LIFETIME_OPTIONS: { value: 1 | 2 | 3; label: string }[] = [
  { value: 1, label: '1 year' },
  { value: 2, label: '2 years' },
  { value: 3, label: '3 years' },
]

type Tab = 'buzz' | 'storage' | 'brainer' | 'lens'

// ── Reusable key table ────────────────────────────────────────────────────────

interface KeyRow {
  id: string
  name: string
  keyPrefix: string
  serviceId?: string
  isActive: boolean
  expiresAt: string | null
  createdAt: string
}

function KeyTable({
  keys,
  isLoading,
  service,
  onRevoke,
}: {
  keys: KeyRow[]
  isLoading: boolean
  service: string
  onRevoke: (id: string, name: string) => void
}) {
  const fmt = useDateFmt()
  if (isLoading) return <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
  if (keys.length === 0) return (
    <EmptyState icon={KeyRound} title="No API keys" description={`Create an API key to authenticate with ${service}`} />
  )
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Name</th>
          {keys.some((k) => k.serviceId) && <th>Service ID</th>}
          <th>Key</th>
          <th>Status</th>
          <th>Expires</th>
          <th>Created</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {keys.map((k) => {
          const status = keyStatus(k.isActive, k.expiresAt)
          return (
            <tr key={k.id}>
              <td className="font-medium text-ink">{k.name}</td>
              {keys.some((row) => row.serviceId) && (
                <td className="font-mono text-xs text-ink-dim">{k.serviceId}</td>
              )}
              <td className="font-mono text-xs text-ink-dim">{k.keyPrefix}••••••••</td>
              <td>
                <Badge variant={status === 'active' ? 'success' : 'danger'}>
                  {status === 'active' ? 'Active' : status === 'expired' ? 'Expired' : 'Revoked'}
                </Badge>
              </td>
              <td className="text-ink-dim">{k.expiresAt ? fmt(k.expiresAt) : ''}</td>
              <td className="text-ink-dim">{fmt(k.createdAt)}</td>
              <td>
                {status === 'active' && (
                  <button
                    onClick={() => onRevoke(k.id, k.name)}
                    className="flex items-center gap-1 text-xs text-ink-dim hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={13} /> Revoke
                  </button>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

// ── Create modal ──────────────────────────────────────────────────────────────

const SERVICE_META: Record<string, { description: string; color: string }> = {
  BUZZ:    { description: 'Event bus  publish and subscribe to events', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  Storage: { description: 'Document storage  upload and retrieve files',  color: 'bg-sky-50 text-sky-700 border-sky-200' },
  Brainer: { description: 'Data model  unattended service-to-service access on your behalf', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  Lens:    { description: 'ML service  unattended forecast/scenario runs on your behalf', color: 'bg-amber-50 text-amber-700 border-amber-200' },
}

function ServiceBadge({ service }: { service: string }) {
  const meta = SERVICE_META[service]
  if (!meta) return null
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs font-medium ${meta.color}`}>
      <KeyRound size={13} />
      <span>{service}</span>
      <span className="font-normal opacity-70">· {meta.description}</span>
    </div>
  )
}

function CreateModal({
  service,
  requireServiceId,
  onClose,
  onSubmit,
  isPending,
  created,
  error,
}: {
  service: string
  requireServiceId?: boolean
  onClose: () => void
  onSubmit: (data: { name: string; serviceId?: string; lifetimeYears: 1 | 2 | 3 }) => void
  isPending: boolean
  created: { key: string; name: string; expiresAt: string | null } | null
  error: string
}) {
  const fmt = useDateFmt()
  const [form, setForm] = useState<{ name: string; serviceId: string; lifetimeYears: 1 | 2 | 3 }>({ name: '', serviceId: '', lifetimeYears: 1 })
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    if (!created) return
    navigator.clipboard.writeText(created.key)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Modal title={`New ${service} API key`} onClose={onClose} size="sm">
      {created ? (
        <div className="space-y-4">
          <ServiceBadge service={service} />
          <p className="text-sm text-ink">
            Your API key has been created. Copy it now  <strong>it will not be shown again.</strong>
          </p>
          <div className="flex items-center gap-2 bg-paper-sunken border border-paper-border rounded-md px-3 py-2">
            <code className="flex-1 text-xs font-mono text-ink break-all">{created.key}</code>
            <button onClick={handleCopy} className="shrink-0 text-ink-dim hover:text-ink transition-colors" title="Copy">
              {copied ? <Check size={15} className="text-emerald-500" /> : <Copy size={15} />}
            </button>
          </div>
          <div className="text-xs text-ink-dim space-y-0.5">
            <p>Name: <span className="font-medium text-ink">{created.name}</span></p>
            <p>Expires: <span className="font-medium text-ink">{created.expiresAt ? fmt(created.expiresAt) : ''}</span></p>
          </div>
          <div className="flex justify-end pt-1">
            <Button onClick={onClose}>Done</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <ServiceBadge service={service} />
          <Field label="Name">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Mail service integration"
              className="field"
            />
          </Field>
          {requireServiceId && (
            <Field label="Service ID">
              <input
                type="text"
                value={form.serviceId}
                onChange={(e) => setForm((p) => ({ ...p, serviceId: e.target.value }))}
                placeholder="e.g. order-parser, external-partner-x"
                className="field"
              />
              <p className="text-xs text-ink-dim mt-1">
                Identity this key acts as when checking dataset permissions. Must match what you grant
                access to on a dataset's Permissions tab.
              </p>
            </Field>
          )}
          <Field label="Lifetime">
            <Select
              value={String(form.lifetimeYears)}
              onValueChange={(v) => setForm((p) => ({ ...p, lifetimeYears: Number(v) as 1 | 2 | 3 }))}
            >
              {LIFETIME_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
              ))}
            </Select>
          </Field>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => onSubmit(requireServiceId ? form : { name: form.name, lifetimeYears: form.lifetimeYears })}
              disabled={!form.name || (requireServiceId && !form.serviceId)}
              loading={isPending}
            >
              Create
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ApiKeys() {
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState<Tab>('buzz')

  // BUZZ keys
  const { data: buzzKeys = [], isLoading: buzzLoading } = useQuery({
    queryKey: ['api-keys-buzz'],
    queryFn: () => getApiKeys().then((r) => r.data),
  })
  const [showBuzzCreate, setShowBuzzCreate] = useState(false)
  const [buzzCreated, setBuzzCreated] = useState<BuzzApiKeyCreated | null>(null)
  const [buzzError, setBuzzError] = useState('')
  const buzzCreate = useMutation({
    mutationFn: createApiKey,
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ['api-keys-buzz'] }); setBuzzCreated(res.data) },
    onError: (err: any) => setBuzzError(err.response?.data?.error || 'Failed to create key'),
  })
  const [buzzRevokeTarget, setBuzzRevokeTarget] = useState<{ id: string; name: string } | null>(null)
  const buzzRevoke = useMutation({
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['api-keys-buzz'] }); setBuzzRevokeTarget(null) },
  })

  // Storage keys
  const { data: storageKeys = [], isLoading: storageLoading } = useQuery({
    queryKey: ['api-keys-storage'],
    queryFn: () => getStorageApiKeys().then((r) => r.data),
  })
  const [showStorageCreate, setShowStorageCreate] = useState(false)
  const [storageCreated, setStorageCreated] = useState<StorageApiKeyCreated | null>(null)
  const [storageError, setStorageError] = useState('')
  const storageCreate = useMutation({
    mutationFn: createStorageApiKey,
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ['api-keys-storage'] }); setStorageCreated(res.data) },
    onError: (err: any) => setStorageError(err.response?.data?.error || 'Failed to create key'),
  })
  const [storageRevokeTarget, setStorageRevokeTarget] = useState<{ id: string; name: string } | null>(null)
  const storageRevoke = useMutation({
    mutationFn: (id: string) => revokeStorageApiKey(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['api-keys-storage'] }); setStorageRevokeTarget(null) },
  })

  // Brainer keys
  const { data: brainerKeys = [], isLoading: brainerLoading } = useQuery({
    queryKey: ['api-keys-brainer'],
    queryFn: () => getBrainerApiKeys().then((r) => r.data),
  })
  const [showBrainerCreate, setShowBrainerCreate] = useState(false)
  const [brainerCreated, setBrainerCreated] = useState<BrainerApiKeyCreated | null>(null)
  const [brainerError, setBrainerError] = useState('')
  const brainerCreate = useMutation({
    mutationFn: createBrainerApiKey,
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ['api-keys-brainer'] }); setBrainerCreated(res.data) },
    onError: (err: any) => setBrainerError(err.response?.data?.error || 'Failed to create key'),
  })
  const [brainerRevokeTarget, setBrainerRevokeTarget] = useState<{ id: string; name: string } | null>(null)
  const brainerRevoke = useMutation({
    mutationFn: (id: string) => revokeBrainerApiKey(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['api-keys-brainer'] }); setBrainerRevokeTarget(null) },
  })

  // Lens keys
  const { data: lensKeys = [], isLoading: lensLoading } = useQuery({
    queryKey: ['api-keys-lens'],
    queryFn: () => getLensApiKeys().then((r) => r.data),
  })
  const [showLensCreate, setShowLensCreate] = useState(false)
  const [lensCreated, setLensCreated] = useState<LensApiKeyCreated | null>(null)
  const [lensError, setLensError] = useState('')
  const lensCreate = useMutation({
    mutationFn: createLensApiKey,
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ['api-keys-lens'] }); setLensCreated(res.data) },
    onError: (err: any) => setLensError(err.response?.data?.error || 'Failed to create key'),
  })
  const [lensRevokeTarget, setLensRevokeTarget] = useState<{ id: string; name: string } | null>(null)
  const lensRevoke = useMutation({
    mutationFn: (id: string) => revokeLensApiKey(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['api-keys-lens'] }); setLensRevokeTarget(null) },
  })

  const tabs: { id: Tab; label: string }[] = [
    { id: 'buzz', label: 'BUZZ' },
    { id: 'storage', label: 'Storage' },
    { id: 'brainer', label: 'Brainer' },
    { id: 'lens', label: 'Lens' },
  ]

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">API Keys</h1>
          <p className="text-sm text-ink-dim mt-0.5">Service authentication keys</p>
        </div>
        <Button
          icon={Plus}
          onClick={() => {
            if (activeTab === 'buzz') { setShowBuzzCreate(true); setBuzzCreated(null); setBuzzError('') }
            else if (activeTab === 'storage') { setShowStorageCreate(true); setStorageCreated(null); setStorageError('') }
            else if (activeTab === 'brainer') { setShowBrainerCreate(true); setBrainerCreated(null); setBrainerError('') }
            else { setShowLensCreate(true); setLensCreated(null); setLensError('') }
          }}
        >
          New API key
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-paper-border">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.id
                ? 'border-accent text-accent'
                : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="table-wrap">
        {activeTab === 'buzz' ? (
          <KeyTable
            keys={buzzKeys}
            isLoading={buzzLoading}
            service="BUZZ"
            onRevoke={(id, name) => setBuzzRevokeTarget({ id, name })}
          />
        ) : activeTab === 'storage' ? (
          <KeyTable
            keys={storageKeys}
            isLoading={storageLoading}
            service="Storage"
            onRevoke={(id, name) => setStorageRevokeTarget({ id, name })}
          />
        ) : activeTab === 'brainer' ? (
          <KeyTable
            keys={brainerKeys}
            isLoading={brainerLoading}
            service="Brainer"
            onRevoke={(id, name) => setBrainerRevokeTarget({ id, name })}
          />
        ) : (
          <KeyTable
            keys={lensKeys}
            isLoading={lensLoading}
            service="Lens"
            onRevoke={(id, name) => setLensRevokeTarget({ id, name })}
          />
        )}
      </div>

      {/* BUZZ modals */}
      {showBuzzCreate && (
        <CreateModal
          service="BUZZ"
          onClose={() => { setShowBuzzCreate(false); setBuzzCreated(null) }}
          onSubmit={(data) => { setBuzzError(''); buzzCreate.mutate(data) }}
          isPending={buzzCreate.isPending}
          created={buzzCreated ? { key: buzzCreated.key, name: buzzCreated.name, expiresAt: buzzCreated.expiresAt } : null}
          error={buzzError}
        />
      )}
      {buzzRevokeTarget && (
        <ConfirmDialog
          title="Revoke API key"
          message={`Revoke "${buzzRevokeTarget.name}"? Any service using this key will immediately lose access.`}
          confirmLabel="Revoke"
          onConfirm={() => buzzRevoke.mutate(buzzRevokeTarget.id)}
          onCancel={() => setBuzzRevokeTarget(null)}
        />
      )}

      {/* Storage modals */}
      {showStorageCreate && (
        <CreateModal
          service="Storage"
          requireServiceId
          onClose={() => { setShowStorageCreate(false); setStorageCreated(null) }}
          onSubmit={(data) => { setStorageError(''); storageCreate.mutate(data as { name: string; serviceId: string; lifetimeYears: 1 | 2 | 3 }) }}
          isPending={storageCreate.isPending}
          created={storageCreated ? { key: storageCreated.key, name: storageCreated.name, expiresAt: storageCreated.expiresAt } : null}
          error={storageError}
        />
      )}
      {storageRevokeTarget && (
        <ConfirmDialog
          title="Revoke API key"
          message={`Revoke "${storageRevokeTarget.name}"? Any service using this key will immediately lose access.`}
          confirmLabel="Revoke"
          onConfirm={() => storageRevoke.mutate(storageRevokeTarget.id)}
          onCancel={() => setStorageRevokeTarget(null)}
        />
      )}

      {/* Brainer modals */}
      {showBrainerCreate && (
        <CreateModal
          service="Brainer"
          onClose={() => { setShowBrainerCreate(false); setBrainerCreated(null) }}
          onSubmit={(data) => { setBrainerError(''); brainerCreate.mutate(data as { name: string; lifetimeYears: 1 | 2 | 3 }) }}
          isPending={brainerCreate.isPending}
          created={brainerCreated ? { key: brainerCreated.key, name: brainerCreated.name, expiresAt: brainerCreated.expiresAt } : null}
          error={brainerError}
        />
      )}
      {brainerRevokeTarget && (
        <ConfirmDialog
          title="Revoke API key"
          message={`Revoke "${brainerRevokeTarget.name}"? Any service using this key will immediately lose access.`}
          confirmLabel="Revoke"
          onConfirm={() => brainerRevoke.mutate(brainerRevokeTarget.id)}
          onCancel={() => setBrainerRevokeTarget(null)}
        />
      )}

      {/* Lens modals */}
      {showLensCreate && (
        <CreateModal
          service="Lens"
          onClose={() => { setShowLensCreate(false); setLensCreated(null) }}
          onSubmit={(data) => { setLensError(''); lensCreate.mutate(data as { name: string; lifetimeYears: 1 | 2 | 3 }) }}
          isPending={lensCreate.isPending}
          created={lensCreated ? { key: lensCreated.key, name: lensCreated.name, expiresAt: lensCreated.expiresAt } : null}
          error={lensError}
        />
      )}
      {lensRevokeTarget && (
        <ConfirmDialog
          title="Revoke API key"
          message={`Revoke "${lensRevokeTarget.name}"? Any service using this key will immediately lose access.`}
          confirmLabel="Revoke"
          onConfirm={() => lensRevoke.mutate(lensRevokeTarget.id)}
          onCancel={() => setLensRevokeTarget(null)}
        />
      )}
    </div>
  )
}
